<template>
  <!-- ── Hero ──────────────────────────────────────────── -->
  <section
    class="hero-section"
    role="region"
    aria-label="Golden Key Imobiliária"
  >
    <div class="hero-static">
      <Transition name="skeleton-fade">
        <div
          v-if="!heroImageLoaded"
          class="carousel-skeleton"
          aria-hidden="true"
        >
          <div class="skeleton-shimmer"></div>
        </div>
      </Transition>
      <img
        src="https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/goldenkey.jpg"
        class="hero-img"
        alt="Golden Key"
        loading="eager"
        @load="heroImageLoaded = true"
        @error="heroImageLoaded = true"
      />
      <div class="slide-gradient" aria-hidden="true"></div>
    </div>

    <!-- Search bar — overlaps carousel bottom on desktop, stacks below on mobile -->
    <div class="search-bar-anchor" ref="searchPanelRef">
      <div class="search-panel">
        <!-- Location -->
        <div class="search-field" @click="focusLocation">
          <div class="search-field__icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <div class="search-field__body">
            <label class="search-field__label" @click.stop="focusLocation"
              >Localização</label
            >
            <input
              ref="locationRef"
              v-model="filtros.localizacao"
              id="sf-location"
              class="search-field__input"
              placeholder="Cidade, bairro ou rua…"
              type="text"
              autocomplete="off"
            />
          </div>
        </div>

        <div class="search-divider" aria-hidden="true"></div>

        <!-- Property type -->
        <div
          class="search-field search-field--dropdown"
          :class="{ 'is-open': openDropdown === 'tipo' }"
          @click="toggleDropdown('tipo')"
        >
          <div class="search-field__icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div class="search-field__body">
            <label class="search-field__label">Tipo de Imóvel</label>
            <div
              class="search-field__value"
              :class="{ 'is-placeholder': !filtros.tipoImovel }"
            >
              {{
                tiposImovel.find((t) => t.value === filtros.tipoImovel)
                  ?.label || "Selecione o tipo…"
              }}
            </div>
          </div>
          <svg
            class="search-field__chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <!-- Dropdown panel -->
          <Transition name="dropdown-fade">
            <div v-if="openDropdown === 'tipo'" class="sf-dropdown" @click.stop>
              <div
                v-for="t in tiposImovel"
                :key="t.value"
                class="sf-dropdown__item"
                :class="{ 'is-selected': filtros.tipoImovel === t.value }"
                @click="pickOption('tipoImovel', t.value)"
              >
                <span>{{ t.label }}</span>
                <svg
                  v-if="filtros.tipoImovel === t.value"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  width="14"
                  height="14"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
          </Transition>
        </div>

        <div class="search-divider" aria-hidden="true"></div>

        <!-- Bedrooms -->
        <div
          class="search-field search-field--dropdown"
          :class="{ 'is-open': openDropdown === 'quartos' }"
          @click="toggleDropdown('quartos')"
        >
          <div class="search-field__icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" />
              <path d="M2 14h20" />
              <path d="M7 14v6" />
              <path d="M17 14v6" />
              <rect x="5" y="4" width="14" height="8" rx="1" />
            </svg>
          </div>
          <div class="search-field__body">
            <label class="search-field__label">Quartos</label>
            <div
              class="search-field__value"
              :class="{ 'is-placeholder': !filtros.quartos }"
            >
              {{
                filtros.quartos
                  ? `${filtros.quartos} quarto${
                      filtros.quartos !== "1" ? "s" : ""
                    }`
                  : "Nº de quartos…"
              }}
            </div>
          </div>
          <svg
            class="search-field__chevron"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
          <!-- Dropdown panel -->
          <Transition name="dropdown-fade">
            <div
              v-if="openDropdown === 'quartos'"
              class="sf-dropdown"
              @click.stop
            >
              <div
                v-for="q in opcoesQuartos"
                :key="q"
                class="sf-dropdown__item"
                :class="{ 'is-selected': filtros.quartos === q }"
                @click="pickOption('quartos', q)"
              >
                <span>{{ q }} quarto{{ q !== "1" ? "s" : "" }}</span>
                <svg
                  v-if="filtros.quartos === q"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  width="14"
                  height="14"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
          </Transition>
        </div>

        <!-- CTA -->
        <button
          class="search-cta"
          @click="handleSearch"
          aria-label="Pesquisar imóveis"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="20"
            height="20"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span>Pesquisar</span>
        </button>
      </div>
    </div>
  </section>
  <!-- Botão para anunciar neste espaço -->
  <div class="advertise-container">
    <q-btn
      size="sm"
      padding="xs"
      rounded
      label="Anuncie neste espaço"
      class="advertise-btn"
      @click="irParaSuporte"
    />
  </div>
  <!-- ── Ads Carousel ──────────────────────────────────── -->

  <section class="ads-section" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
    <div class="ads-wrap">
      <!-- Skeleton -->
      <div v-if="carouselLoading" class="ads-skeleton">
        <div class="skeleton-shimmer"></div>
      </div>

      <template v-else-if="carouselItems.length > 0">
        <!-- Slides -->
        <div class="ads-track">
          <div
            v-for="(slide, i) in carouselItems"
            :key="i"
            class="ads-slide"
            :class="{
              'is-active': currentSlide === i,
              'ads-slide--clickable': slide.link,
            }"
            :aria-hidden="currentSlide !== i"
            @click="slide.link && openSlideLink(slide)"
          >
            <img
              :src="
                isMobile && slide.mobile_image_url
                  ? slide.mobile_image_url
                  : slide.image_url
              "
              :alt="slide.title || 'Anúncio'"
              class="ads-slide__img"
              :loading="i === 0 ? 'eager' : 'lazy'"
            />
            <div class="ads-slide__gradient" aria-hidden="true"></div>
            <div v-if="slide.title" class="ads-slide__content">
              <span class="ads-slide__title">{{ slide.title }}</span>
            </div>
          </div>
        </div>

        <!-- Chevrons -->
        <button
          v-if="carouselItems.length > 1"
          class="ads-chevron ads-chevron--prev"
          @click.stop="prevSlide"
          aria-label="Slide anterior"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="16"
            height="16"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          v-if="carouselItems.length > 1"
          class="ads-chevron ads-chevron--next"
          @click.stop="nextSlide"
          aria-label="Próximo slide"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            width="16"
            height="16"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <!-- Dots -->
        <div v-if="carouselItems.length > 1" class="ads-dots">
          <button
            v-for="(_, i) in carouselItems"
            :key="i"
            class="ads-dot"
            :class="{ 'is-active': currentSlide === i }"
            @click.stop="goToSlide(i)"
            :aria-label="`Slide ${i + 1}`"
          ></button>
        </div>
      </template>
    </div>
  </section>

  <!-- ── Properties grid ───────────────────────────────── -->
  <div class="properties-section q-pa-md">
    <!-- ── Properties grid ───────────────────────────────── -->
    <div class="properties-section q-pa-md">
      <div class="container">
        <div class="q-mb-xl text-center">
          <h2 class="text-h3 text-weight-bold text-grey-9 q-mb-sm">
            Imóveis em Destaque
          </h2>
          <p class="text-body1 text-grey-7">
            Encontre o imóvel perfeito para você
          </p>
        </div>

        <!-- Skeleton -->
        <div v-if="loader" class="row q-col-gutter-lg">
          <div
            v-for="n in 8"
            :key="'skeleton-' + n"
            class="col-12 col-sm-6 col-md-3"
          >
            <q-card flat bordered class="imovel-card">
              <q-skeleton height="180px" square />
              <q-card-section>
                <q-skeleton type="text" width="40%" class="q-mb-xs" />
                <q-skeleton type="text" width="80%" class="q-mb-xs" />
                <q-skeleton type="text" width="60%" class="q-mb-md" />
                <div class="row q-gutter-md q-mb-md">
                  <q-skeleton type="text" width="30px" />
                  <q-skeleton type="text" width="30px" />
                  <q-skeleton type="text" width="40px" />
                  <q-skeleton type="text" width="30px" />
                </div>
                <q-separator class="q-my-md" />
                <div class="row items-center justify-between">
                  <q-skeleton type="text" width="50%" />
                  <q-skeleton type="QBtn" />
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <!-- Featured properties -->
        <div v-else class="row q-col-gutter-lg">
          <div
            v-for="(imovel, index) in imoveis"
            :key="index"
            class="col-12 col-sm-6 col-md-3"
          >
            <q-card class="imovel-card" flat bordered>
              <div class="badge-container">
                <q-badge
                  :color="imovel.objective === 'Venda' ? 'green-7' : 'blue-7'"
                  floating
                  class="tipo-negocio-badge"
                >
                  {{ imovel.objective }}
                </q-badge>
              </div>

              <div class="image-container">
                <q-img
                  :src="imovel.primary_image_url"
                  :ratio="16 / 10"
                  class="cursor-pointer"
                  @click="verDetalhes(imovel)"
                >
                  <template v-slot:loading>
                    <q-skeleton class="fit" />
                  </template>
                </q-img>
                <span class="featured-badge">
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="11"
                    height="11"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    />
                  </svg>
                  Destaque
                </span>
              </div>

              <q-card-section>
                <div class="text-overline text-primary q-mb-xs">
                  {{ imovel.property_type }}
                </div>
                <div class="text-h6 text-weight-bold q-mb-sm ellipsis-2-lines">
                  {{ imovel.title }}
                </div>
                <div class="row items-center q-mb-md text-grey-7">
                  <q-icon name="location_on" size="18px" class="q-mr-xs" />
                  <span class="text-body2">{{ imovel.location }}</span>
                </div>
                <div class="row items-center q-gutter-md q-mb-md">
                  <div class="row items-center">
                    <q-icon name="bed" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium">{{
                      imovel.bedroom_amount
                    }}</span>
                  </div>
                  <div class="row items-center">
                    <q-icon name="bathtub" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium">{{
                      imovel.bathroom_amount
                    }}</span>
                  </div>
                  <div class="row items-center">
                    <q-icon name="square_foot" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium"
                      >{{ imovel.area }}m²</span
                    >
                  </div>
                  <div class="row items-center">
                    <q-icon name="garage" size="20px" color="grey-7" />
                    <span class="q-ml-xs text-body2 text-weight-medium">{{
                      imovel.garage_spaces
                    }}</span>
                  </div>
                </div>
                <q-separator class="q-my-md" />
                <div class="row items-center justify-between">
                  <div>
                    <div class="text-h5 text-weight-bold text-secondary">
                      {{ formatPrice(imovel.price, imovel.currency) }}
                    </div>
                  </div>
                  <q-btn
                    round
                    color="secondary"
                    icon="arrow_forward"
                    size="md"
                    @click="verDetalhes(imovel)"
                  />
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <div class="row justify-center q-mt-xl">
          <button class="see-all-btn" @click="router.push('/imoveis-search')">
            Ver todos os imóveis
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="16"
              height="16"
              aria-hidden="true"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Why Us ────────────────────────────────────────── -->
    <section class="why-section">
      <div class="why-container">
        <div class="why-header">
          <span class="why-eyebrow">Por que nos escolher</span>
          <h2 class="why-title">A Golden Key ao Seu Lado</h2>
          <p class="why-sub">
            Mais do que um portal — somos o seu parceiro de confiança em cada
            etapa da jornada.
          </p>
        </div>

        <div class="why-grid">
          <div
            class="why-card"
            v-for="item in whyItems"
            :key="item.title"
            :style="{ '--accent': item.color }"
          >
            <div class="why-icon">
              <svg
                :viewBox="item.viewBox"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
                width="26"
                height="26"
                aria-hidden="true"
              >
                <path v-for="(d, di) in item.paths" :key="di" :d="d" />
              </svg>
            </div>
            <h3 class="why-card-title">{{ item.title }}</h3>
            <p class="why-card-body">{{ item.body }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useHead } from "@unhead/vue";
import apiClient from "@/config/api";

const router = useRouter();
const drawer = ref(false);

const irParaSuporte = () => {
  drawer.value = false;
  localStorage.setItem("supportSubject", "anunciar");
  router.replace("/suporte");
};

// ── SEO ──────────────────────────────────────────────────
useHead({
  title: "Golden key - Sua imobiliaria de confiança",
  meta: [
    {
      name: "keywords",
      content:
        "imóveis Maputo, casas venda Moçambique, apartamentos alugar Maputo, propriedades Moçambique, imobiliária Maputo, terrenos venda, casas luxo Maputo, apartamentos modernos",
    },
    {
      name: "description",
      content:
        "Sua imobiliária de confiança. Encontre o imóvel dos seus sonhos com as melhores condições do mercado.",
    },
    {
      property: "og:title",
      content: "Golden key - Sua imobiliaria de confiança",
    },
    {
      property: "og:description",
      content:
        "Sua imobiliária de confiança. Encontre o imóvel dos seus sonhos com as melhores condições do mercado.",
    },
    {
      property: "og:image",
      content:
        "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png",
    },
    { name: "geo.region", content: "MZ-MPM" },
    { name: "geo.placename", content: "Maputo" },
    { name: "language", content: "Portuguese" },
    { property: "og:locale", content: "pt_MZ" },
  ],
  link: [
    { rel: "canonical", href: "https://goldenkey.co.mz" },
    { rel: "canonical", href: "https://portal.goldenkey.co.mz" },
  ],
});

// ── Viewport ─────────────────────────────────────────────
const isMobile = ref(window.innerWidth < 768);
function onResize() {
  isMobile.value = window.innerWidth < 768;
}

// ── Hero ──────────────────────────────────────────────────
const heroImageLoaded = ref(false);

// ── Ads Carousel ─────────────────────────────────────────
const carouselItems = ref([]);
const carouselLoading = ref(true);
const currentSlide = ref(0);
let autoTimer = null;
let isPaused = false;
const reducedMotion =
  typeof window !== "undefined"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;

async function fetchCarousel() {
  try {
    const { data } = await apiClient.get("/carousel");
    const items = Array.isArray(data) ? data : data.data ?? [];
    carouselItems.value = items
      .filter((item) => item.active)
      .sort((a, b) => a.order - b.order);
  } catch {
    carouselItems.value = [];
  } finally {
    carouselLoading.value = false;
    if (!reducedMotion && carouselItems.value.length > 1) startAuto();
  }
}

function startAuto() {
  autoTimer = setInterval(() => {
    if (!isPaused) nextSlide();
  }, 7500);
}

function pauseAuto() {
  isPaused = true;
}
function resumeAuto() {
  isPaused = false;
}

function nextSlide() {
  if (!carouselItems.value.length) return;
  currentSlide.value = (currentSlide.value + 1) % carouselItems.value.length;
}

function prevSlide() {
  if (!carouselItems.value.length) return;
  currentSlide.value =
    (currentSlide.value - 1 + carouselItems.value.length) %
    carouselItems.value.length;
}

function goToSlide(i) {
  currentSlide.value = i;
}

function openSlideLink(slide) {
  if (!slide.link) return;
  if (slide.link.startsWith("http")) {
    window.open(slide.link, "_blank", "noopener,noreferrer");
  } else {
    router.push(slide.link);
  }
}

// ── Search ────────────────────────────────────────────────
const filtros = ref({
  localizacao: "",
  tipoImovel: null,
  quartos: null,
});

const tiposImovel = [
  { label: "Apartamento", value: 1 },
  { label: "Casa", value: 2 },
  { label: "Moradia", value: 3 },
  { label: "Terreno", value: 4 },
  { label: "Casa de Praia", value: 6 },
  { label: "Outro", value: 7 },
];

const opcoesQuartos = ["1", "2", "3", "4", "5+"];

const openDropdown = ref(null);
const locationRef = ref(null);
const searchPanelRef = ref(null);

function focusLocation() {
  locationRef.value?.focus();
}

function toggleDropdown(key) {
  openDropdown.value = openDropdown.value === key ? null : key;
}

function pickOption(field, value) {
  filtros.value[field] = value;
  openDropdown.value = null;
}

function handleOutsideClick(e) {
  if (searchPanelRef.value && !searchPanelRef.value.contains(e.target)) {
    openDropdown.value = null;
  }
}

const handleSearch = () => {
  openDropdown.value = null;
  router.push({
    name: "imoveis-search",
    query: {
      location: filtros.value.localizacao || undefined,
      property_type: filtros.value.tipoImovel || undefined,
      bedroom_amount: filtros.value.quartos || undefined,
    },
  });
};

// ── Properties list ───────────────────────────────────────
const loader = ref(false);
const imoveis = ref([]);

async function fetchFeatured() {
  loader.value = true;
  try {
    const { data } = await apiClient.get("/properties/featured");
    imoveis.value = Array.isArray(data) ? data : data.data ?? [];
  } catch {
    imoveis.value = [];
  } finally {
    loader.value = false;
  }
}

const formatPrice = (price, currency) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
  }).format(price);
};

const verDetalhes = (imovel) => {
  console.log("Ver detalhes do imóvel:", imovel);
  router.push({ name: "imovel-details", params: { slug: imovel.slug } });
};

// ── Why Us ────────────────────────────────────────────────
const whyItems = [
  {
    title: "Experiência Comprovada",
    body: "Anos de presença no mercado imobiliário moçambicano com uma equipa especializada que conhece cada bairro, cada oportunidade.",
    color: "#027BE3",
    viewBox: "0 0 24 24",
    paths: ["M12 2L2 7l10 5 10-5-10-5z", "M2 17l10 5 10-5", "M2 12l10 5 10-5"],
  },
  {
    title: "Portfólio Exclusivo",
    body: "Centenas de imóveis selecionados — apartamentos modernos, moradias de luxo, terrenos e espaços comerciais em todo o país.",
    color: "#EBA301",
    viewBox: "0 0 24 24",
    paths: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z", "M9 22V12h6v10"],
  },
  {
    title: "Transparência Total",
    body: "Processos claros, documentação segura e acompanhamento em cada fase — da visita ao contrato, sem surpresas.",
    color: "#10b981",
    viewBox: "0 0 24 24",
    paths: ["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z", "M9 12l2 2 4-4"],
  },
  {
    title: "Suporte Dedicado",
    body: "A nossa equipa está disponível para responder às suas dúvidas e guiá-lo nas melhores decisões, sempre ao seu ritmo.",
    color: "#8b5cf6",
    viewBox: "0 0 24 24",
    paths: [
      "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
      "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
      "M23 21v-2a4 4 0 0 0-3-3.87",
      "M16 3.13a4 4 0 0 1 0 7.75",
    ],
  },
];

onMounted(() => {
  fetchCarousel();
  fetchFeatured();
  document.addEventListener("click", handleOutsideClick);
  window.addEventListener("resize", onResize);
});
onUnmounted(() => {
  if (autoTimer) clearInterval(autoTimer);
  document.removeEventListener("click", handleOutsideClick);
  window.removeEventListener("resize", onResize);
});
</script>

<style scoped>
/* ══════════════════════════════════════════════════════
   Hero / Carousel
══════════════════════════════════════════════════════ */
.hero-section {
  position: relative;
  width: 100%;
}

/* ── Static hero ────────────────────────── */
.hero-static {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 65vh;
  overflow: hidden;
}

.hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.slide-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(8, 12, 24, 0.88) 0%,
    rgba(8, 12, 24, 0.45) 30%,
    rgba(8, 12, 24, 0.12) 60%,
    transparent 100%
  );
  pointer-events: none;
}

/* ── Hero skeleton ──────────────────────── */
.carousel-skeleton {
  position: absolute;
  inset: 0;
  z-index: 2;
  background: linear-gradient(135deg, #0f172a 0%, #1a2744 55%, #0f2a4a 100%);
  overflow: hidden;
}

.skeleton-shimmer {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.04) 50%,
    transparent 100%
  );
  background-size: 200% 100%;
  animation: shimmer 2.2s ease-in-out infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.skeleton-fade-leave-active {
  transition: opacity 0.5s ease;
}
.skeleton-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-shimmer {
    animation: none;
  }
}

/* ══════════════════════════════════════════════════════
   Ads Carousel
══════════════════════════════════════════════════════ */
.ads-section {
  padding: 8px 2rem 0;
  max-width: 1400px;
  margin: 0 auto;
}

.ads-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 1;
  max-height: 160px;
  border-radius: 16px;
  overflow: hidden;
  background: #0a1628;
}

/* ── Ads skeleton ───────────────────────── */
.ads-skeleton {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a2744 0%, #0f172a 100%);
  overflow: hidden;
}

/* ── Ads slides ─────────────────────────── */
.ads-track {
  position: absolute;
  inset: 0;
}

.ads-slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 700ms ease-in-out;
}

.ads-slide.is-active {
  opacity: 1;
  z-index: 2;
}

.ads-slide--clickable {
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  .ads-slide {
    transition: none;
  }
}

.ads-slide__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.ads-slide__gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to right,
    rgba(8, 12, 24, 0.65) 0%,
    rgba(8, 12, 24, 0.25) 50%,
    transparent 100%
  );
  pointer-events: none;
}

.ads-slide__content {
  position: absolute;
  top: 50%;
  left: 36px;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ads-slide__title {
  font-family: "Georgia", serif;
  font-size: clamp(1rem, 2vw, 1.35rem);
  font-weight: 400;
  color: #ffffff;
  letter-spacing: 0.03em;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.3;
  max-width: 480px;
}

.ads-slide__cta {
  display: inline-flex;
  align-items: center;
  padding: 7px 18px;
  background: #eba301;
  color: #ffffff;
  text-decoration: none;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border-radius: 6px;
  width: fit-content;
  transition: background 0.2s ease, transform 0.2s ease;
}

.ads-slide__cta:hover {
  background: #d49200;
  transform: translateY(-1px);
}

/* ── Ads chevrons ───────────────────────── */
.ads-chevron {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease;
  padding: 0;
}

.ads-wrap:hover .ads-chevron {
  opacity: 1;
}
.ads-chevron:hover {
  background: rgba(255, 255, 255, 0.28);
}
.ads-chevron--prev {
  left: 14px;
}
.ads-chevron--next {
  right: 14px;
}

/* ── Ads dots ───────────────────────────── */
.ads-dots {
  position: absolute;
  bottom: 12px;
  right: 18px;
  z-index: 10;
  display: flex;
  gap: 6px;
  align-items: center;
}

.ads-dot {
  height: 6px;
  width: 6px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.35);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: width 0.3s ease, background 0.3s ease;
}

.ads-dot.is-active {
  width: 20px;
  background: #eba301;
}

/* ── Search bar ─────────────────────────── */
.search-bar-anchor {
  position: relative;
  z-index: 20;
  margin: -32px auto 0;
  padding: 0 2rem;
  max-width: 1100px;
}

.search-panel {
  display: flex;
  align-items: stretch;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.16), 0 4px 16px rgba(0, 0, 0, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.6);
}

.search-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  cursor: text;
  transition: background 0.18s ease;
}

.search-field:hover {
  background: rgba(2, 123, 227, 0.03);
}

.search-field__icon {
  flex-shrink: 0;
  color: #eba301;
  display: flex;
  align-items: center;
}

.search-field__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.search-field__label {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #94a3b8;
  pointer-events: none;
}

.search-field__input {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  padding: 0;
  line-height: 1.4;
}

.search-field__input::placeholder {
  color: #cbd5e1;
  font-weight: 400;
}

.search-field__select {
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
}

.search-field__select option {
  color: #0f172a;
}

.search-divider {
  width: 1px;
  background: rgba(0, 0, 0, 0.07);
  margin: 14px 0;
  flex-shrink: 0;
}

.search-cta {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 10px 10px 10px 10px;
  padding: 0 28px;
  background: #027be3;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
}

.search-cta:hover {
  background: #0256a0;
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(2, 123, 227, 0.38);
}

/* ── Custom dropdown ────────────────────── */
.search-field--dropdown {
  position: relative;
  cursor: pointer;
  user-select: none;
}

.search-field--dropdown.is-open {
  background: rgba(2, 123, 227, 0.04);
}

.search-field__value {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0f172a;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-field__value.is-placeholder {
  color: #cbd5e1;
  font-weight: 400;
}

.search-field__chevron {
  flex-shrink: 0;
  color: #94a3b8;
  transition: transform 0.22s ease, color 0.18s ease;
  margin-left: auto;
}

.search-field--dropdown.is-open .search-field__chevron {
  transform: rotate(180deg);
  color: #027be3;
}

.sf-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  min-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12), 0 2px 10px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 6px;
  z-index: 100;
  overflow: hidden;
}

.sf-dropdown__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: background 0.14s ease, color 0.14s ease;
  white-space: nowrap;
}

.sf-dropdown__item:hover {
  background: #f0f7ff;
  color: #027be3;
}

.sf-dropdown__item.is-selected {
  color: #027be3;
  background: rgba(2, 123, 227, 0.06);
}

.sf-dropdown__item.is-selected svg {
  color: #027be3;
  flex-shrink: 0;
}

/* Dropdown enter/leave transition */
.dropdown-fade-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.dropdown-fade-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ══════════════════════════════════════════════════════
   Properties section
══════════════════════════════════════════════════════ */
.properties-section {
  padding-top: 56px !important;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
}

.see-all-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 32px;
  background: transparent;
  border: 1.5px solid #eba301;
  color: #eba301;
  border-radius: 10px;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}

.see-all-btn:hover {
  background: #eba301;
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(235, 163, 1, 0.32);
}

.see-all-btn svg {
  transition: transform 0.2s ease;
}

.see-all-btn:hover svg {
  transform: translateX(4px);
}

/* ══════════════════════════════════════════════════════
   Property cards (unchanged)
══════════════════════════════════════════════════════ */
.imovel-card {
  height: 100%;
  transition: all 0.3s ease;
  border-radius: 16px;
  overflow: hidden;
}

.imovel-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

.image-container {
  position: relative;
  overflow: hidden;
}

.badge-container {
  position: relative;
}

.tipo-negocio-badge {
  position: absolute;
  top: 225px;
  left: 225px;
  z-index: 1;
  padding: 6px 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.featured-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: 6px;
  background: #eba301;
  color: #ffffff;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(235, 163, 1, 0.45);
}

.ellipsis-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 56px;
}

.q-img {
  transition: transform 0.3s ease;
}
.imovel-card:hover .q-img {
  transform: scale(1.05);
}

/* ══════════════════════════════════════════════════════
   Responsive
══════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  /* stack below hero on mobile, no overlap */
  .search-bar-anchor {
    margin: 0 auto;
    padding: 0.75rem;
  }

  .search-panel {
    flex-direction: column;
    border-radius: 14px;
  }

  .search-divider {
    width: auto;
    height: 1px;
    margin: 0 18px;
  }

  .search-field {
    padding: 14px 18px;
  }

  .search-cta {
    margin: 10px;
    padding: 14px;
    border-radius: 10px;
    justify-content: center;
  }

  .properties-section {
    padding-top: 16px !important;
  }

  .ads-section {
    padding: 16px 0.75rem 0;
  }
  .ads-wrap {
    border-radius: 12px;
  }
  .ads-slide__content {
    left: 20px;
    gap: 8px;
  }
}

/* ══════════════════════════════════════════════════════
   Why Us
══════════════════════════════════════════════════════ */
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap");

.why-section {
  background: #f8fafc;
  padding: 96px 2rem;
}

.why-container {
  max-width: 1200px;
  margin: 0 auto;
}

.why-header {
  text-align: center;
  margin-bottom: 64px;
}

.why-eyebrow {
  display: inline-block;
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #eba301;
  margin-bottom: 14px;
}

.why-title {
  font-family: "Georgia", "Palatino Linotype", serif;
  font-size: clamp(1.9rem, 3.5vw, 2.75rem);
  font-weight: 400;
  color: #0f172a;
  margin: 0 0 16px;
  line-height: 1.2;
}

.why-sub {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 1rem;
  color: #64748b;
  max-width: 540px;
  margin: 0 auto;
  line-height: 1.7;
}

.why-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 28px;
}

.why-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 36px 28px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.why-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.09);
}

.why-icon {
  width: 52px;
  height: 52px;
  border-radius: 13px;
  background: color-mix(in srgb, var(--accent) 10%, transparent);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 22px;
}

.why-card-title {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px;
  line-height: 1.3;
}

.why-card-body {
  font-family: "Plus Jakarta Sans", system-ui, sans-serif;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.7;
  margin: 0;
}

@media (max-width: 1024px) {
  .why-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .why-section {
    padding: 64px 1.25rem;
  }
  .why-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .why-header {
    margin-bottom: 40px;
  }
}

/* ── Advertise button ───────────────────────────── */

.advertise-container {
  display: flex;
  justify-content: center;
  padding: 18px 0 10px;
}

/* ── Advertise button ───────────────────────────── */

.advertise-container {
  display: flex;
  justify-content: flex-start;
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 2rem 2px;
}

.advertise-btn {
  background: #eba301;
  color: white;
  border: none;
  padding: 10px 26px;
  border-radius: 8px;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.03em;
  transition: all 0.25s ease;
}

.advertise-btn:hover {
  background: #d49200;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(235, 163, 1, 0.3);
}
</style>
